import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _last from 'lodash/last';
import _union from 'lodash/union';
import moment from 'moment-timezone';
import { store } from 'src/main';
import {
  calculateWeeksBetweenWeekIds,
  capWeekIdToValidWeek,
  goBackNWeeksSDS,
  getComparisonWeekId,
  getPreviousWeekId,
  getStartAndEndOfWeek,
  dateToSDSWeekId,
  is53WeeksInTheYear
} from 'src/utils/dateUtils';
import { panic } from 'src/utils/mixpanel';
import { isDrive } from './app';
import { getQueryParams } from 'src/utils/browser';
import { ComparisonPeriod } from 'src/utils/Hooks';

const currentYear = 1;
const timeOffset = new Date().getTimezoneOffset() * 60000;
const currentStartWeek = 38;
const startYear = 2016;
export const DAY_ID_FORMAT = 'YYYYMMDD';

export const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const getDayIdFromDate = (date) => +moment(date).format(DAY_ID_FORMAT);

function addDays(dateValue, days) {
  const tmpDate = new Date(dateValue);
  tmpDate.setDate(tmpDate.getDate() + days);
  return tmpDate;
}

export const formatTime = (value, formatStr = 'MM/DD/YY HH:mm:ss') => {
  return moment(value).format(formatStr);
};

/**
 * Returns a string representing the format of the formatStr, based on unix timestamp`.
 */
export const formatUnixToTime = (value, formatStr = 'MM/DD/YYYY') => {
  return moment.unix(value).format(formatStr);
};

export const getFirstWeekOfYear = (weekId) => weekId - (weekId % 100) + 1;

export const getLastWeekOfYear = (weekId) => {
  const year = Math.floor(weekId / 100);
  const lastWeekId = weekId - (weekId % 100) + (is53WeeksInTheYear(year) ? 53 : 52);
  return lastWeekId;
};

export const getLastWeek = (weekIds) => weekIds[weekIds.length - 1];

export const getLastWeekPreviousYear = (weekIds) => {
  const lastWeek = getLastWeek(weekIds);
  const previousYear = Math.floor((lastWeek - 100) / 100) * 100;
  return weekIds.reduce((prevVal, currVal) => {
    if (currVal > prevVal && Math.floor(currVal / 100) === Math.floor(previousYear / 100)) {
      return currVal;
    }
    return prevVal;
  }, previousYear);
};

//  This function should be replaced with the Comparison Time Period function
export function getPreviousDateRange(mainTimePeriod, weekIds, comparisonTimePeriod) {
  const currentPeriodStartWeekId = mainTimePeriod.startWeek;
  const currentPeriodEndWeekId = mainTimePeriod.endWeek;
  let difference = 0;
  const currentPeriodStartWeekYear = Math.floor(currentPeriodStartWeekId / 100);
  const currentPeriodEndWeekYear = Math.floor(currentPeriodEndWeekId / 100);
  const lastWeekPreviousYear = getLastWeekPreviousYear(weekIds);
  const firstWeekCurrentYear = getFirstWeekOfYear(currentPeriodEndWeekId);
  if (currentPeriodStartWeekYear === currentPeriodEndWeekYear) {
    difference = currentPeriodEndWeekId - currentPeriodStartWeekId;
  } else {
    difference = currentPeriodEndWeekId - firstWeekCurrentYear + 1 + (lastWeekPreviousYear - currentPeriodStartWeekId);
  }

  if (comparisonTimePeriod.id === 'prior-year') {
    return {
      startWeek: mainTimePeriod.startWeek - 100,
      endWeek: mainTimePeriod.endWeek - 100,
      difference
    };
  }

  const previousPeriodEndWeekId =
    (currentPeriodStartWeekId - 1) % 100 < 1 ? lastWeekPreviousYear : currentPeriodStartWeekId - 1;
  let previousPeriodStartWeekId = null;
  if ((previousPeriodEndWeekId - difference) % 100 < 1 || (previousPeriodEndWeekId - difference) % 100 > 60) {
    const remainder = previousPeriodEndWeekId - firstWeekCurrentYear + 1;
    previousPeriodStartWeekId = lastWeekPreviousYear - (difference - remainder);
    if (weekIds.indexOf(previousPeriodStartWeekId) === 1) {
      previousPeriodStartWeekId = getFirstWeekOfYear(lastWeekPreviousYear);
    }
  } else {
    previousPeriodStartWeekId = previousPeriodEndWeekId - difference;
  }

  return {
    startWeek: previousPeriodStartWeekId,
    endWeek: previousPeriodEndWeekId,
    difference
  };
}

export const getFirstWeekFirstYear = (weekIds) =>
  weekIds.reduce((prevVal, curVal) => (curVal < prevVal ? curVal : prevVal), 400000);

export function getWeekIdMinValue(weekIds, mainTimePeriod) {
  if (mainTimePeriod.id === 'ytd' || mainTimePeriod.id === 'ly') {
    return getFirstWeekOfYear(getLastWeek(weekIds));
  }

  const period = mainTimePeriod.id.toLowerCase() === 'lw' ? '1w' : mainTimePeriod.id;
  const weeksToGoBack = parseInt(period.replace(/w/gi, ''), 10);
  if (weekIds.length >= weeksToGoBack) {
    return weekIds[weekIds.length - weeksToGoBack];
  }
  return weekIds[0];
}

export const getWeekIdRangeFilter = (weekIds, mainTimePeriod) => ({
  fieldName: 'weekId',
  minValue:
    mainTimePeriod && mainTimePeriod.startWeek ? mainTimePeriod.startWeek : getWeekIdMinValue(weekIds, mainTimePeriod),
  maxValue: mainTimePeriod && mainTimePeriod.endWeek ? mainTimePeriod.endWeek : getLastWeek(weekIds)
});

/**
 * Returns a `Date` representing the first day of the provided `weekId`.
 * Uses SDS-based date calculations for proper week handling including week 53.
 */
export function getWeekFirstDate(weekId): Date {
  const { startDate } = getStartAndEndOfWeek(weekId, false);
  if (startDate) {
    return moment(startDate, 'YYYYMMDD').toDate();
  }

  // Fallback for invalid weekId - cap to valid week
  const year = Math.floor(weekId / 100);
  const week = weekId % 100;
  const maxWeeks = is53WeeksInTheYear(year) ? 53 : 52;
  const cappedWeekId = year * 100 + Math.min(week, maxWeeks);
  const { startDate: cappedStartDate } = getStartAndEndOfWeek(cappedWeekId, false);
  if (cappedStartDate) {
    return moment(cappedStartDate, 'YYYYMMDD').toDate();
  }

  // Ultimate fallback to old logic if SDS fails
  console.warn('[getWeekFirstDate] SDS fallback for weekId:', weekId);
  const hasExtraWeek = weekId % 100 === 53;
  const weekIdToUse = hasExtraWeek ? weekId - 1 : weekId;
  const day = moment(`${weekIdToUse}`, 'YYYYWW').day('Sunday');
  const dayToUse = hasExtraWeek ? day.add(1, 'week') : day;
  return dayToUse.toDate();
}

export function capDateToCurrentDate(date) {
  if (date > new Date()) {
    return addDays(new Date(), -1);
  }
  return date;
}

/**
 * Returns a `Date` representing the last day of the provided `weekId`.
 * Uses SDS-based date calculations for proper week handling including week 53.
 */
export function getWeekLastDate(weekId): Date {
  const { endDate } = getStartAndEndOfWeek(weekId, false);
  if (endDate) {
    return moment(endDate, 'YYYYMMDD').toDate();
  }

  // Fallback for invalid weekId - cap to valid week
  const year = Math.floor(weekId / 100);
  const week = weekId % 100;
  const maxWeeks = is53WeeksInTheYear(year) ? 53 : 52;
  const cappedWeekId = year * 100 + Math.min(week, maxWeeks);
  const { endDate: cappedEndDate } = getStartAndEndOfWeek(cappedWeekId, false);
  if (cappedEndDate) {
    return moment(cappedEndDate, 'YYYYMMDD').toDate();
  }

  // Ultimate fallback to old logic if SDS fails
  console.warn('[getWeekLastDate] SDS fallback for weekId:', weekId);
  const ISOweekStart = getWeekFirstDate(weekId);
  const weekEndDate = addDays(ISOweekStart, 6 - (ISOweekStart as Date).getDay());
  return weekEndDate;
}

export const getLastDayPreviousYear = (weekIds) => {
  const lastWeek = getLastWeek(weekIds);
  // this is 31st december of last year. This date will always exist
  return Math.floor((lastWeek - 100) / 100) * 10000 + 1231;
};

/**
 * @deprecated Use getStartAndEndOfWeek instead (you can wrap it with formatNumericDateToMonthDayYear or formatDateStringWithSuffix )
 */
export function getWeekLastDay(week, year, asDayId = false) {
  const currDate = getWeekLastDate(week);

  if (asDayId) {
    return +moment(currDate).format(DAY_ID_FORMAT);
  }

  if (year) {
    return `${monthNames[(currDate as Date).getMonth()]} ${(currDate as Date).getDate()}, ${(
      currDate as Date
    ).getFullYear()}`;
  }

  return `${monthNames[(currDate as Date).getMonth()]} ${(currDate as Date).getDate()}`;
}

/**
 * Calculates the number of weeks between two weekIds (inclusive).
 * Uses calculateWeeksBetweenWeekIds which properly handles the SDS week system.
 */
export const subtractWeeks = (startWeek: number, endWeek: number): number => {
  return calculateWeeksBetweenWeekIds(startWeek, endWeek);
};

/**
 * Goes back N weeks from a given weekId using the SDS week system.
 * Properly handles year boundaries and week 53.
 */
export const goBackNWeeks = (endWeek: number, numberOfWeeks: number): number => {
  return goBackNWeeksSDS(endWeek, numberOfWeeks);
};

/**
 * Calculates the comparison period's startWeek based on the main period's week range.
 * Ensures the comparison period has the same number of weeks as the main period.
 *
 * @param mainStartWeek - The main period's start week
 * @param mainEndWeek - The main period's end week
 * @param comparisonEndWeek - The comparison period's end week (already capped to valid week)
 * @returns The comparison period's start week
 */
export const computeComparisonStartWeek = (
  mainStartWeek: number,
  mainEndWeek: number,
  comparisonEndWeek: number
): number => {
  // Calculate the number of weeks in the main period
  const mainPeriodWeekCount = subtractWeeks(mainStartWeek, mainEndWeek) - 1;
  // weeksToGoBack: 0 means startWeek = endWeek, 1 means 1 week before endWeek, etc.
  const weeksToGoBack = Math.min(52, Math.max(0, mainPeriodWeekCount - 1));
  return goBackNWeeks(comparisonEndWeek, weeksToGoBack);
};

export function buildCustomDateRangeDisplayName(startDayId, endDayId) {
  const displayName = `${moment(startDayId, DAY_ID_FORMAT).format('MMM D, YYYY')} - ${moment(
    endDayId,
    DAY_ID_FORMAT
  ).format('MMM D, YYYY')}`;

  return displayName;
}

export const updateComparisonTimePeriodForExports = async (mainTimePeriod, comparisonTimePeriod) => {
  const mainPeriod = _cloneDeep(mainTimePeriod);
  const comparisonPeriod = _cloneDeep(mainTimePeriod);

  // Use API to get proper comparison week
  comparisonPeriod.endWeek = await getComparisonWeekId(mainTimePeriod.endWeek);
  comparisonPeriod.startWeek = computeComparisonStartWeek(
    mainTimePeriod.startWeek,
    mainTimePeriod.endWeek,
    comparisonPeriod.endWeek
  );
  comparisonPeriod.shortDisplayName = 'Prior-Year';

  // Make sure the same range but different year.
  if (comparisonTimePeriod.id === 'prior-year') {
    // For prior-year, get the comparison week for the already-compared endWeek
    comparisonPeriod.endWeek = await getComparisonWeekId(comparisonPeriod.endWeek);
    comparisonPeriod.startWeek = computeComparisonStartWeek(
      mainTimePeriod.startWeek,
      mainTimePeriod.endWeek,
      comparisonPeriod.endWeek
    );

    comparisonPeriod.startDayId = +moment(`${comparisonPeriod.startWeek}`, 'YYYYWW').format(DAY_ID_FORMAT);

    comparisonPeriod.endDayId = +moment(`${comparisonPeriod.endWeek}`, 'YYYYWW').format(DAY_ID_FORMAT);

    comparisonPeriod.displayName = buildCustomDateRangeDisplayName(
      comparisonPeriod.startDayId,
      comparisonPeriod.endDayId
    );

    comparisonPeriod.startWeekStartDate = moment(`${comparisonPeriod.startDayId}`).toDate();
    comparisonPeriod.endWeekEndDate = moment(`${comparisonPeriod.endDayId}`).toDate();
  }

  return {
    mainPeriod,
    comparisonPeriod
  };
};

const computeDriveComparisonTimePeriod = (mainTimePeriod, comparisonTimeWindow) => {
  const timePeriodToReturn = _cloneDeep(mainTimePeriod);
  timePeriodToReturn.id = comparisonTimeWindow;
  // Prior-Period
  if (comparisonTimeWindow === ComparisonPeriod.PRIOR_PERIOD) {
    timePeriodToReturn.shortDisplayName = 'Prior-Period';
    // MTD
    if (mainTimePeriod.id === 'mtd') {
      // The end date of prior period will be 1 day before the start date of the main time period
      // It is also the last date of the previous month, since main time period is MTD
      timePeriodToReturn.endDayId = parseInt(
        moment(`${mainTimePeriod.startDayId}`).subtract(1, 'day').format(DAY_ID_FORMAT),
        10
      );
      // the difference between the start and end date of the main time period will be used for prior period
      const daysDiff = moment(`${mainTimePeriod.endDayId}`).diff(moment(`${mainTimePeriod.startDayId}`), 'days');
      timePeriodToReturn.startDayId = parseInt(
        moment(`${timePeriodToReturn.endDayId}`).subtract(daysDiff, 'days').format(DAY_ID_FORMAT),
        10
      );

      timePeriodToReturn.startWeekStartDate = moment(`${timePeriodToReturn.startDayId}`).toDate();
      timePeriodToReturn.endWeekEndDate = moment(`${timePeriodToReturn.endDayId}`).toDate();
      timePeriodToReturn.startWeek = dateToSDSWeekId(timePeriodToReturn.startWeekStartDate);
      timePeriodToReturn.endWeek = dateToSDSWeekId(timePeriodToReturn.endWeekEndDate);

      return timePeriodToReturn;
    }
    // WEEKS
    else {
      const weeksInTimeWindow = subtractWeeks(mainTimePeriod.startWeek, mainTimePeriod.endWeek) + 1;
      timePeriodToReturn.endWeek = mainTimePeriod.startWeek - weeksInTimeWindow;
    }
    const mainStartDayId = Number(mainTimePeriod.startDayId);
    const mainEndDayId = Number(mainTimePeriod.endDayId);
    const hasValidMainDayRange = Number.isFinite(mainStartDayId) && Number.isFinite(mainEndDayId);

    // Fallback for cases where start/end day IDs are temporarily unavailable (can happen during period switches).
    // Derive a valid date range directly from the computed comparison weeks to avoid "Invalid Date - Invalid Date".
    if (!hasValidMainDayRange) {
      const { startDate } = getStartAndEndOfWeek(timePeriodToReturn.startWeek);
      const { endDate } = getStartAndEndOfWeek(timePeriodToReturn.endWeek);

      timePeriodToReturn.startDayId = Number(startDate);
      timePeriodToReturn.endDayId = Number(endDate);
      timePeriodToReturn.startWeekStartDate = moment(startDate, DAY_ID_FORMAT).toDate();
      timePeriodToReturn.endWeekEndDate = moment(endDate, DAY_ID_FORMAT).toDate();
      timePeriodToReturn.displayName = buildCustomDateRangeDisplayName(
        timePeriodToReturn.startDayId,
        timePeriodToReturn.endDayId
      );
      return timePeriodToReturn;
    }

    const daysDiff = moment(`${mainEndDayId}`, DAY_ID_FORMAT).diff(moment(`${mainStartDayId}`, DAY_ID_FORMAT), 'days');
    const endWeekEndDate = moment(`${mainStartDayId}`, DAY_ID_FORMAT).subtract(1, 'day').toDate();
    const startWeekStartDate = moment(`${mainStartDayId}`, DAY_ID_FORMAT)
      .subtract(daysDiff + 1, 'day')
      .toDate();

    timePeriodToReturn.startWeekStartDate = startWeekStartDate;
    timePeriodToReturn.endWeekEndDate = endWeekEndDate;
    timePeriodToReturn.startDayId = +moment(startWeekStartDate).format(DAY_ID_FORMAT);
    timePeriodToReturn.endDayId = +moment(endWeekEndDate).format(DAY_ID_FORMAT);
    return timePeriodToReturn;
  }

  // -- Prior-Year --
  timePeriodToReturn.endWeek -= 100;
  // Cap the endWeek to a valid week in case the prior year has fewer weeks (e.g., 52 vs 53)
  timePeriodToReturn.endWeek = capWeekIdToValidWeek(timePeriodToReturn.endWeek);
  timePeriodToReturn.startWeek = computeComparisonStartWeek(
    mainTimePeriod.startWeek,
    mainTimePeriod.endWeek,
    timePeriodToReturn.endWeek
  );
  timePeriodToReturn.shortDisplayName = 'Prior-Year';

  // YTD && Prior-Year
  if (mainTimePeriod.id === 'ytd') {
    timePeriodToReturn.startDayId = parseInt(moment().subtract(1, 'year').startOf('year').format(DAY_ID_FORMAT), 10);
    // When it is YTD and Prior Year, comparison period will be end of the same day of the previous year, but not year end of last year
    timePeriodToReturn.endDayId = parseInt(
      moment(`${mainTimePeriod.endDayId}`).subtract(1, 'year').format(DAY_ID_FORMAT),
      10
    );

    timePeriodToReturn.startWeekStartDate = moment(`${timePeriodToReturn.startDayId}`).toDate();
    timePeriodToReturn.endWeekEndDate = moment(`${timePeriodToReturn.endDayId}`).toDate();
    return timePeriodToReturn;
  }
  // MTD
  else if (mainTimePeriod.id === 'mtd') {
    timePeriodToReturn.startDayId = moment(mainTimePeriod.startDayId, 'YYYYMMDD')
      .subtract(1, 'year')
      .format('YYYYMMDD');
    timePeriodToReturn.startWeekStartDate = moment(`${timePeriodToReturn.startDayId}`).toDate();

    timePeriodToReturn.endDayId = parseInt(
      moment(`${mainTimePeriod.endDayId}`).subtract(1, 'year').format(DAY_ID_FORMAT),
      10
    );
    timePeriodToReturn.endWeekEndDate = moment(`${timePeriodToReturn.endDayId}`).toDate();
    return timePeriodToReturn;
  }
  // Rest: Weeks (All type) and Custom Date Range (CD)
  else {
    timePeriodToReturn.startDayId = parseInt(
      moment(`${mainTimePeriod.startDayId}`, DAY_ID_FORMAT).subtract(1, 'year').format(DAY_ID_FORMAT),
      10
    );
    timePeriodToReturn.endDayId = parseInt(
      moment(`${mainTimePeriod.endDayId}`, DAY_ID_FORMAT).subtract(1, 'year').format(DAY_ID_FORMAT),
      10
    );

    timePeriodToReturn.startWeekStartDate = moment(`${timePeriodToReturn.startDayId}`).toDate();
    timePeriodToReturn.endWeekEndDate = moment(`${timePeriodToReturn.endDayId}`).toDate();
    return timePeriodToReturn;
  }
};

/**
 * NEW FUNCTIONS TO CALCULATE COMPARISON TIME PERIOD
 * @param {string} comparisonTimeWindow is an id string ie: 'prior-year' or 'prior-period'
 */
export async function computeComparisonTimePeriod(allWeekIds, mainTimePeriod, comparisonTimeWindow) {
  const isAd = getQueryParams('tab') === 'advertising';
  if (isDrive || isAd) {
    return computeDriveComparisonTimePeriod(mainTimePeriod, comparisonTimeWindow);
  }
  const timePeriodToReturn = _cloneDeep(mainTimePeriod);
  timePeriodToReturn.id = comparisonTimeWindow;
  // Prior-Period

  if (comparisonTimeWindow === 'prior-period') {
    timePeriodToReturn.shortDisplayName = 'Prior-Period';
    const startWeekIdIndex = allWeekIds.indexOf(mainTimePeriod.startWeek);
    const diffBetweenWeekIds = calculateWeeksBetweenWeekIds(mainTimePeriod.startWeek, mainTimePeriod.endWeek) - 1;
    if (startWeekIdIndex === 0) {
      timePeriodToReturn.startWeek -= 100;
      timePeriodToReturn.endWeek -= 100;
      // Cap to valid weeks in case the prior year has fewer weeks (e.g., 52 vs 53)
      timePeriodToReturn.startWeek = capWeekIdToValidWeek(timePeriodToReturn.startWeek);
      timePeriodToReturn.endWeek = capWeekIdToValidWeek(timePeriodToReturn.endWeek);
    } else if (mainTimePeriod.id === 'mtd') {
      const dayRange = moment(`${mainTimePeriod.endDayId}`).diff(moment(`${mainTimePeriod.startDayId}`), 'days');
      timePeriodToReturn.endDayId = parseInt(
        moment(`${mainTimePeriod.startDayId}`).subtract(1, 'day').format(DAY_ID_FORMAT),
        10
      );
      timePeriodToReturn.startDayId = parseInt(
        moment(`${timePeriodToReturn.endDayId}`).subtract(dayRange, 'day').format(DAY_ID_FORMAT),
        10
      );

      timePeriodToReturn.startWeekStartDate = moment(`${timePeriodToReturn.startDayId}`).toDate();
      timePeriodToReturn.endWeekEndDate = moment(`${timePeriodToReturn.endDayId}`).toDate();
      timePeriodToReturn.startWeek = dateToSDSWeekId(timePeriodToReturn.startWeekStartDate);
      timePeriodToReturn.endWeek = dateToSDSWeekId(timePeriodToReturn.endWeekEndDate);
      timePeriodToReturn.displayName = buildCustomDateRangeDisplayName(
        timePeriodToReturn.startDayId,
        timePeriodToReturn.endDayId
      );
      return timePeriodToReturn;
    } else {
      const weeksInTimeWindow = diffBetweenWeekIds + 1;
      timePeriodToReturn.endWeek = getPreviousWeekId(mainTimePeriod.startWeek, 1);
      if (startWeekIdIndex - weeksInTimeWindow < 0) {
        [timePeriodToReturn.startWeek] = allWeekIds;
      } else {
        timePeriodToReturn.startWeek = getPreviousWeekId(timePeriodToReturn.endWeek, diffBetweenWeekIds);
      }
    }

    const mainStartDayId = Number(mainTimePeriod.startDayId);
    const mainEndDayId = Number(mainTimePeriod.endDayId);
    const hasValidMainDayRange = Number.isFinite(mainStartDayId) && Number.isFinite(mainEndDayId);

    // Fallback for cases where start/end day IDs are temporarily unavailable (can happen during period switches).
    // Derive a valid date range directly from the computed comparison weeks to avoid "Invalid Date - Invalid Date".
    if (!hasValidMainDayRange) {
      const { startDate } = getStartAndEndOfWeek(timePeriodToReturn.startWeek);
      const { endDate } = getStartAndEndOfWeek(timePeriodToReturn.endWeek);

      timePeriodToReturn.startDayId = Number(startDate);
      timePeriodToReturn.endDayId = Number(endDate);
      timePeriodToReturn.startWeekStartDate = moment(startDate, DAY_ID_FORMAT).toDate();
      timePeriodToReturn.endWeekEndDate = moment(endDate, DAY_ID_FORMAT).toDate();
      timePeriodToReturn.displayName = buildCustomDateRangeDisplayName(
        timePeriodToReturn.startDayId,
        timePeriodToReturn.endDayId
      );
      return timePeriodToReturn;
    }

    const daysDiff = moment(`${mainEndDayId}`, DAY_ID_FORMAT).diff(moment(`${mainStartDayId}`, DAY_ID_FORMAT), 'days');
    const endWeekEndDate = moment(`${mainStartDayId}`, DAY_ID_FORMAT).subtract(1, 'day').toDate();
    const startWeekStartDate = moment(`${mainStartDayId}`, DAY_ID_FORMAT)
      .subtract(daysDiff + 1, 'day')
      .toDate();

    timePeriodToReturn.startWeekStartDate = startWeekStartDate;
    timePeriodToReturn.endWeekEndDate = endWeekEndDate;
    timePeriodToReturn.startDayId = +moment(startWeekStartDate).format(DAY_ID_FORMAT);
    timePeriodToReturn.endDayId = +moment(endWeekEndDate).format(DAY_ID_FORMAT);
    timePeriodToReturn.displayName = buildCustomDateRangeDisplayName(
      timePeriodToReturn.startDayId,
      timePeriodToReturn.endDayId
    );
    return timePeriodToReturn;
  }

  // Prior-Year
  timePeriodToReturn.endWeek = await getComparisonWeekId(mainTimePeriod.endWeek);

  if (mainTimePeriod.id === 'ytd') {
    // For YTD, map start independently to align with prior year start
    timePeriodToReturn.startWeek = await getComparisonWeekId(mainTimePeriod.startWeek);
  } else {
    // For all other periods (ly, 13w, 4w, cd, etc.), preserve the same week count
    const mainWeekCount = calculateWeeksBetweenWeekIds(mainTimePeriod.startWeek, mainTimePeriod.endWeek);
    timePeriodToReturn.startWeek = getPreviousWeekId(timePeriodToReturn.endWeek, mainWeekCount - 1);
  }

  timePeriodToReturn.shortDisplayName = 'Prior-Year';

  if (mainTimePeriod.id === 'mtd') {
    timePeriodToReturn.startDayId = parseInt(
      moment(`${mainTimePeriod.startDayId}`).subtract(1, 'year').format(DAY_ID_FORMAT),
      10
    );
    timePeriodToReturn.startWeekStartDate = moment(`${timePeriodToReturn.startDayId}`).toDate();

    timePeriodToReturn.endDayId = parseInt(
      moment(`${mainTimePeriod.endDayId}`).subtract(1, 'year').format(DAY_ID_FORMAT),
      10
    );

    timePeriodToReturn.endWeekEndDate = moment(`${timePeriodToReturn.endDayId}`).toDate();

    return timePeriodToReturn;
  }
  timePeriodToReturn.startDayId = getStartAndEndOfWeek(timePeriodToReturn.startWeek).startDate;
  timePeriodToReturn.endDayId = getStartAndEndOfWeek(timePeriodToReturn.endWeek).endDate;

  timePeriodToReturn.startWeekStartDate = moment(`${timePeriodToReturn.startDayId}`).toDate();
  timePeriodToReturn.endWeekEndDate = moment(`${timePeriodToReturn.endDayId}`).toDate();
  timePeriodToReturn.displayName = buildCustomDateRangeDisplayName(
    timePeriodToReturn.startDayId,
    timePeriodToReturn.endDayId
  );
  return timePeriodToReturn;
}

export function getDay(dayId) {
  const date = new Date(dayId);
  return `${monthNames[date.getMonth()]} ${date.getDate()}`;
}

export function getWeekNumber(date) {
  // Copy date so don't modify original
  const d = new Date(+date);
  d.setHours(0, 0, 0);
  // Set to nearest Thursday: current date + 4 - current day number
  // Make Sunday's day number 7
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  // Get first day of year
  const yearStart = new Date(d.getFullYear(), 0, 1);
  // Calculate full weeks to nearest Thursday
  const weekNo = Math.ceil((((d as any) - (yearStart as any)) / 86400000 + 1) / 7);
  // Return array of year and week number
  return [d.getFullYear(), weekNo, d.getFullYear() * 100 + weekNo];
}

export function getFormattedWeekNumber(date) {
  let clonedDate = date;
  if (clonedDate.week === undefined) {
    clonedDate = moment(clonedDate);
  }
  // if week number is 1 and month is 11 (december), then add 1 to year to handle year switch
  let [year, week] = [clonedDate.year(), clonedDate.isoWeek()];
  if (clonedDate.month() === 11 && clonedDate.isoWeek() === 1) {
    [year, week] = [clonedDate.year() + 1, clonedDate.isoWeek()];
  } else if (clonedDate.month() === 0 && clonedDate.isoWeek() === 53) {
    [year, week] = [clonedDate.year() - 1, clonedDate.isoWeek()];
  }

  return `${year}${week < 10 ? `0${week}` : week}`;
}

export const getFirstWeekOfMonth = (weekId) => {
  const startDate = getWeekFirstDate(weekId);
  const monthStartDate = moment(startDate).startOf('month').toDate();
  return +getFormattedWeekNumber(monthStartDate);
};

function addStartAndEndDate(timePeriod) {
  const { startDate } = getStartAndEndOfWeek(timePeriod.startWeek, false);
  const { endDate } = getStartAndEndOfWeek(timePeriod.endWeek, false);
  timePeriod.startDayId = +startDate;
  timePeriod.endDayId = +endDate;
  // Parse dates with explicit format to avoid ambiguity. Use local timezone at noon
  // to avoid edge cases at midnight while preserving the calendar date.
  timePeriod.startWeekStartDate = moment(startDate, DAY_ID_FORMAT).hour(12).toDate();
  timePeriod.endWeekEndDate = moment(endDate, DAY_ID_FORMAT).hour(12).toDate();
}

function computeDatesForLastMonth(timePeriodToReturn) {
  const lastMonth = moment().subtract(1, 'month');
  timePeriodToReturn.startWeekStartDate = lastMonth.startOf('month').toDate();
  timePeriodToReturn.endWeekEndDate = lastMonth.endOf('month').toDate();
  timePeriodToReturn.startWeek = dateToSDSWeekId(timePeriodToReturn.startWeekStartDate);
  timePeriodToReturn.endWeek = dateToSDSWeekId(timePeriodToReturn.endWeekEndDate);
  timePeriodToReturn.startDayId = getDayIdFromDate(timePeriodToReturn.startWeekStartDate);
  timePeriodToReturn.endDayId = getDayIdFromDate(timePeriodToReturn.endWeekEndDate);
}

function computeDatesForLast3Months(timePeriodToReturn) {
  const lastMonth = moment().subtract(1, 'month');
  const threeMonthsAgo = moment().subtract(3, 'month');

  timePeriodToReturn.startWeekStartDate = threeMonthsAgo.startOf('month').toDate();
  timePeriodToReturn.endWeekEndDate = lastMonth.endOf('month').toDate();
  timePeriodToReturn.startWeek = dateToSDSWeekId(timePeriodToReturn.startWeekStartDate);
  timePeriodToReturn.endWeek = dateToSDSWeekId(timePeriodToReturn.endWeekEndDate);

  timePeriodToReturn.startDayId = parseInt(moment().startOf('year').format(DAY_ID_FORMAT), 10);
  timePeriodToReturn.endDayId = parseInt(moment().subtract(1, 'day').format(DAY_ID_FORMAT), 10);
}

function computeDatesForPeriodToDateTimePeriodForDailyData(timePeriodToReturn) {
  if (timePeriodToReturn.id === 'ytd') {
    const endWeekEndDate = moment().subtract(1, 'day').toDate();
    timePeriodToReturn.startWeekStartDate = moment(endWeekEndDate).startOf('year').toDate();
    timePeriodToReturn.endWeekEndDate = endWeekEndDate;
    timePeriodToReturn.startWeek = dateToSDSWeekId(timePeriodToReturn.startWeekStartDate);
    timePeriodToReturn.endWeek = dateToSDSWeekId(timePeriodToReturn.endWeekEndDate);
    timePeriodToReturn.startDayId = parseInt(moment().startOf('year').format(DAY_ID_FORMAT), 10);
    timePeriodToReturn.endDayId = parseInt(moment().subtract(1, 'day').format(DAY_ID_FORMAT), 10);
  } else if (timePeriodToReturn.id === 'mtd') {
    const endWeekEndDate = moment().subtract(1, 'day').toDate();
    timePeriodToReturn.startWeekStartDate = moment(endWeekEndDate).startOf('month').toDate();
    timePeriodToReturn.endWeekEndDate = endWeekEndDate;
    timePeriodToReturn.startWeek = dateToSDSWeekId(timePeriodToReturn.startWeekStartDate);
    timePeriodToReturn.endWeek = dateToSDSWeekId(timePeriodToReturn.endWeekEndDate);
    timePeriodToReturn.startDayId = getDayIdFromDate(timePeriodToReturn.startWeekStartDate);
    timePeriodToReturn.endDayId = getDayIdFromDate(timePeriodToReturn.endWeekEndDate);
  } else if (timePeriodToReturn.id === 'lastMonth') {
    computeDatesForLastMonth(timePeriodToReturn);
  } else if (timePeriodToReturn.id === 'last3Months') {
    computeDatesForLast3Months(timePeriodToReturn);
  }
}

function computeDatesForPeriodToDateTimePeriodForWeeklyData(timePeriodToReturn) {
  if (timePeriodToReturn.id === 'ytd') {
    timePeriodToReturn.startWeek = getFirstWeekOfYear(timePeriodToReturn.endWeek);
    addStartAndEndDate(timePeriodToReturn);
  } else if (timePeriodToReturn.id === 'mtd') {
    const endWeekEndDate = moment().subtract(1, 'day').toDate();
    timePeriodToReturn.startWeekStartDate = moment(endWeekEndDate).startOf('month').toDate();
    timePeriodToReturn.endWeekEndDate = endWeekEndDate;
    timePeriodToReturn.startWeek = dateToSDSWeekId(timePeriodToReturn.startWeekStartDate);
    timePeriodToReturn.endWeek = dateToSDSWeekId(timePeriodToReturn.endWeekEndDate);
    timePeriodToReturn.startDayId = getDayIdFromDate(timePeriodToReturn.startWeekStartDate);
    timePeriodToReturn.endDayId = getDayIdFromDate(timePeriodToReturn.endWeekEndDate);
  } else if (timePeriodToReturn.id === 'ly') {
    const previousYearWeekId = timePeriodToReturn.endWeek - 100;
    timePeriodToReturn.startWeek = getFirstWeekOfYear(previousYearWeekId);
    timePeriodToReturn.endWeek = getLastWeekOfYear(previousYearWeekId);
    addStartAndEndDate(timePeriodToReturn);
  } else if (timePeriodToReturn.id === 'lastMonth') {
    computeDatesForLastMonth(timePeriodToReturn);
  } else if (timePeriodToReturn.id === 'last3Months') {
    computeDatesForLast3Months(timePeriodToReturn);
  }
}

/*
@params
  allWeekIds is an Array of integers
  timePeriod is an object { id: string, startWeek: int,  }
 */
export function computeMainTimePeriod(allWeekIds, timePeriod, app) {
  const timePeriodToReturn = _cloneDeep(timePeriod);
  const lastWeekId = _last(allWeekIds);
  const isAd = getQueryParams('tab') === 'advertising';
  if (['cd', 'select1Month', 'select3Months'].includes(timePeriodToReturn.id)) {
    if (!timePeriodToReturn.startWeek || !timePeriodToReturn.endWeek) {
      timePeriodToReturn.startWeek = getFirstWeekOfYear(lastWeekId);
      timePeriodToReturn.endWeek = lastWeekId;
    }
    addStartAndEndDate(timePeriodToReturn);
    return timePeriodToReturn;
  }

  timePeriodToReturn.endWeek = lastWeekId;
  if (['ytd', 'mtd', 'ly', 'lastMonth', 'last3Months'].includes(timePeriodToReturn.id)) {
    if (app.dataFrequency === 'daily' || isAd) {
      computeDatesForPeriodToDateTimePeriodForDailyData(timePeriodToReturn);
    } else {
      computeDatesForPeriodToDateTimePeriodForWeeklyData(timePeriodToReturn);
    }
    return timePeriodToReturn;
  }
  const period = timePeriodToReturn.id.toLowerCase() === '1w' ? '1w' : timePeriodToReturn.id;
  let weeksToGoBack = parseInt(period.replace(/w/gi, ''), 10);
  if (app.dataFrequency === 'daily') {
    weeksToGoBack += 1;
    timePeriodToReturn.endWeek = allWeekIds.length > 1 ? allWeekIds[allWeekIds.length - 2] : _last(allWeekIds);
  }
  if (allWeekIds.length >= weeksToGoBack) {
    timePeriodToReturn.startWeek = allWeekIds[allWeekIds.length - weeksToGoBack];
  } else {
    [timePeriodToReturn.startWeek] = allWeekIds;
  }
  addStartAndEndDate(timePeriodToReturn);
  return timePeriodToReturn;
}

export function getWeekCount(year, monthNumber) {
  // month_number is in the range 1..12
  const firstOfMonth = new Date(year, monthNumber - 1, 1);
  const lastOfMonth = new Date(year, monthNumber, 0);

  const used = firstOfMonth.getDay() + lastOfMonth.getDate();

  return Math.ceil(used / 7);
}

export function parseDate(dateStr) {
  return new Date(new Date(dateStr).getTime() + new Date().getTimezoneOffset() * 60000);
}

export function formatDate(date) {
  return `${date.getDate()}-${monthNames[date.getMonth()]}-${date.getFullYear()}`;
}

export function getDateOfWeek(w, y) {
  const d = 1 + (w - 1) * 7; // 1st of January + 7 days for each week
  return new Date(y, 0, d);
}

export function basicDayFormat(date) {
  const timezone = 'America/Los_Angeles'; // convert all timezones to PST to avoid displaying wrong day in legends
  return moment(date).tz(timezone).format('M/D/YY');
}

/**
 * Returns a string representing the last day of the provided date's month.
 */
export function getLastDayOfMonth(date) {
  return moment(date).endOf('month').format('MMMM D, YYYY');
}

export function getLastDayIdOfMonth(date) {
  return moment(date).endOf('month').format(DAY_ID_FORMAT);
}

export function getNextMonth(month) {
  if (month === 12) {
    return '01';
  }

  if (month < 9) {
    return `0${month + 1}`;
  }

  return month + 1;
}

export function getNextDay(date) {
  const dateString = String(date);
  const year = +dateString.substring(0, 4);
  const month = +dateString.substring(4, 6);
  const day = +dateString.substring(6, 8);

  const lastDayOfMonth = getLastDayIdOfMonth(dateString);
  // case end of year
  if (month === 12 && day === 31) {
    return +`${year + 1}0101`;
  }

  if (dateString === lastDayOfMonth) {
    return +`${year}${getNextMonth(month)}01`;
  }

  return +`${year}${month < 10 ? `0${month}` : month}${day < 9 ? `0${day + 1}` : day + 1}`;
}

export function getNextWeek(date) {
  const dateString = String(date);
  const year = +dateString.substring(0, 4);
  const week = +dateString.substring(4, 6);
  const isYearContain53Weeks = is53WeeksInTheYear(year);

  // case end of year
  if (isYearContain53Weeks ? week === 53 : week === 52) {
    return +`${year + 1}01`;
  }

  return +`${year}${week < 9 ? `0${week + 1}` : week + 1}`;
}

let currentWeekIndex = 1;
const monthNameOfWeek = [];
for (let i = 1; i <= 12; i += 1) {
  const weekCount = getWeekCount(currentYear, i);
  for (let j = currentWeekIndex; j < currentWeekIndex + weekCount; j += 1) {
    monthNameOfWeek[j] = monthNames[i - 1];
  }
  currentWeekIndex += weekCount;
}

export function isBeforeDay(a, b) {
  if (!moment.isMoment(a) || !moment.isMoment(b)) {
    return false;
  }

  const aYear = a.year();
  const aMonth = a.month();

  const bYear = b.year();
  const bMonth = b.month();

  const isSameYear = aYear === bYear;
  const isSameMonth = aMonth === bMonth;

  if (isSameYear && isSameMonth) {
    return a.date() < b.date();
  }
  if (isSameYear) {
    return aMonth < bMonth;
  }
  return aYear < bYear;
}

export function isInclusivelyAfterDay(a, b) {
  if (!moment.isMoment(a) || !moment.isMoment(b)) {
    return false;
  }
  return !isBeforeDay(a, b);
}

export const getAllRetailerWeekIDs = (allWeekIdsByRetailerId) => {
  const allWeekIds = Object.keys(allWeekIdsByRetailerId).map((key) => allWeekIdsByRetailerId[key]);

  // Union of all available weeks across all retailers
  const weekIds = _union(...allWeekIds).sort(); // temporary rollback for Mitch demo
  return weekIds;
};

export const computeUpdatedTimePeriods = async ({
  allWeekIdsByRetailerId,
  mainTimePeriod,
  comparisonTimePeriod,
  retailer,
  app,
  params
}) => {
  const { availableMainTimePeriods } = mainTimePeriod;
  const { availableComparisonTimePeriods } = comparisonTimePeriod;
  const allWeekIds =
    retailer.id === '0' ? getAllRetailerWeekIDs(allWeekIdsByRetailerId) : allWeekIdsByRetailerId[retailer.id];
  if (!allWeekIds) {
    return panic(`Tried to get week IDs for retailer id ${retailer.id} but none exist.`);
  }
  const weekIds =
    retailer.id === '0' ? getAllRetailerWeekIDs(allWeekIdsByRetailerId) : allWeekIdsByRetailerId[retailer.id];
  if (!weekIds) {
    return panic(`No available Week IDs for the currently selected retailer ID: ${retailer.id}`);
  }
  const comparisonId =
    params.pid !== undefined ? `${params.pid}` : app.name === 'advertising' ? 'prior-period' : app.comparisonDefault;

  const hasWeekRange = params.sw !== undefined && params.ew !== undefined && params.wr !== undefined;

  let selectedMainTimePeriod = hasWeekRange
    ? availableMainTimePeriods.find((item) => _get(item, 'id') === params.wr)
    : availableMainTimePeriods.find((x) => x.default) || availableMainTimePeriods[0];

  // Fallback to default time range if we're navigating from AMC dashboard to a summary page via one of the product grids
  if (params.wr === '2w' && params.tab !== 'connectDashboard') {
    selectedMainTimePeriod = availableMainTimePeriods.find((x) => x.default);
  }

  // Fallback when the URL's wr param doesn't match any available time period
  // (can happen during initialization race conditions before availableMainTimePeriods is fully populated)
  if (!selectedMainTimePeriod) {
    selectedMainTimePeriod = availableMainTimePeriods.find((x) => x.default) || availableMainTimePeriods[0];
  }

  const { shortDisplayName, id } = selectedMainTimePeriod;
  const newTimePeriod = computeMainTimePeriod(weekIds, selectedMainTimePeriod, app);
  // CD is initialized to be YTD to begin with
  let { endWeek, endDayId, endWeekEndDate } = newTimePeriod;

  // What if the last week is not in the available weeks? We should use the default value (last available week for retailer)
  const newEndWeek = hasWeekRange && params.wr === 'cd' ? parseInt(params.ew, 10) : endWeek;
  const newEndDayId = hasWeekRange && params.wr === 'cd' && params.edid ? parseInt(params.edid, 10) : endDayId;
  const newEndWeekEndDate =
    hasWeekRange && params.wr === 'cd' && params.edid ? moment(params.edid).toDate() : endWeekEndDate;

  if (allWeekIdsByRetailerId[retailer.id].includes(newEndWeek)) {
    // If week id is contained in list of retailer's weeks, use this value
    endWeek = newEndWeek; // only set end week, if it's a valid end week
    endDayId = newEndDayId;
    endWeekEndDate = newEndWeekEndDate;
  } // else use default value

  // Clamp startWeek to the retailer's available weekIds. computeMainTimePeriod
  // can return a startWeek that's outside the retailer's data window when the
  // selected period (e.g. "Last Year") is longer than the retailer's history,
  // or undefined/0/NaN if the period failed to resolve. Falling back to the
  // oldest available week keeps startWeek a valid, truthy id so downstream
  // consumers (and the `!mainTimePeriod.startWeek` guards in *AuthLayout) stay
  // consistent. Mirrors the existing fallback in computeMainTimePeriod
  // (see `[timePeriodToReturn.startWeek] = allWeekIds;`).
  //
  // Without this clamp, a falsy startWeek kept the bootstrap guards in
  // HomeAuthLayout / AdHomeAuthLayout truthy on every render, dispatching
  // the same fetches in a tight loop. In one prod incident the per-tab
  // request rate reached ~1240/min and tripped AWS WAF rate-limiting,
  // which locked the affected users out of every Stackline app on the
  // shared API host until the WAF window expired.
  let { startWeek } = newTimePeriod;
  const { startDayId, startWeekStartDate } = newTimePeriod;
  if (!startWeek || !weekIds.includes(startWeek)) {
    [startWeek] = weekIds;
  }
  const updatedMainPeriod = {
    startWeek: hasWeekRange && params.wr === 'cd' ? parseInt(params.sw, 10) : startWeek,
    endWeek,
    startDayId: hasWeekRange && params.wr === 'cd' && params.sdid ? parseInt(params.sdid, 10) : startDayId,
    endDayId,
    startWeekStartDate:
      hasWeekRange && params.wr === 'cd' && params.sdid ? moment(params.sdid).toDate() : startWeekStartDate,
    endWeekEndDate,
    id,
    shortDisplayName
  };

  const updatedComparisonPeriod = await computeComparisonTimePeriod(weekIds, updatedMainPeriod, comparisonId);
  updatedComparisonPeriod.availableComparisonTimePeriods = availableComparisonTimePeriods;
  updatedComparisonPeriod.comparisonIndex = availableComparisonTimePeriods.findIndex((x) => x.id === comparisonId);

  return { updatedMainPeriod, updatedComparisonPeriod, startWeek, endWeek, allWeekIds };
};

/**
 * Given a time period object, returns a key that is used to identify a piece of data from that time period.
 *
 * @param {object} timePeriod A time period object like `mainTimePeriod` or `comparisonTimePeriod` from Redux
 * @param {string?} key The base key for the beginning of the created key; defaults of `"value"`
 */
export const getDataKeyForTimePeriod = (timePeriod, key = 'value') =>
  `${key}_${timePeriod.startWeek}_${timePeriod.endWeek}`;

const buildDayIdRangeFilter = (startDate, endDate, rawData = false) => ({
  fieldName: 'dayId',
  minValue: rawData ? startDate : getDayIdFromDate(startDate),
  maxValue: rawData ? endDate : getDayIdFromDate(endDate)
});

/**
 * Builds a `RangeFilter` by `dayId` representing the current month from start to end
 */
export const buildFullMonthRangeFilters = () => {
  const startDate = moment().startOf('month').toDate();
  const endDate = moment().endOf('month').toDate();
  const dayIdFilter = buildDayIdRangeFilter(startDate, endDate);
  return [dayIdFilter];
};

/**
 * Builds a `RangeFilter` by `dayId` representing the next month from start to end
 */
export const buildNextFullMonthRangeFilters = () => {
  const startDate = moment().add(1, 'month').startOf('month').toDate();
  const endDate = moment().add(1, 'month').endOf('month').toDate();
  const dayIdFilter = buildDayIdRangeFilter(startDate, endDate);
  return [dayIdFilter];
};

/**
 * Builds a `RangeFilter` by `dayId` for the current MTD time period.
 */
export const buildLast60DaysRangeFilters = () => {
  const startDate = moment().subtract(60, 'days').toDate();
  const endDate = moment();
  const dayIdFilter = buildDayIdRangeFilter(startDate, endDate);
  return [dayIdFilter];
};

export const buildLast30DaysRangeFilters = () => {
  const startDate = moment().subtract(30, 'days').toDate();
  const endDate = moment();
  const dayIdFilter = buildDayIdRangeFilter(startDate, endDate);
  return [dayIdFilter];
};

/**
 * Builds a `RangeFilter` by `dayId` for the current MTD time period.
 */
export const buildMtdRangeFilters = () => {
  const startDate = moment().startOf('month').toDate();
  const endDate = moment().subtract(1, 'day').toDate();
  const dayIdFilter = buildDayIdRangeFilter(startDate, endDate);
  return [dayIdFilter];
};

export const buildMtdComparisonRangeFilters = () => {
  const startDate = moment().subtract(1, 'months').startOf('month').toDate();
  const endDate = moment().subtract(1, 'months');
  const dayIdFilter = buildDayIdRangeFilter(startDate, endDate);
  return [dayIdFilter];
};

export const build4wRangeFilters = () => {
  const { mainTimePeriod } = store.getState();
  const last4WeekPeriod = mainTimePeriod.availableMainTimePeriods.find((x) => x.id === '4w');
  const dayIdFilter = buildDayIdRangeFilter(last4WeekPeriod.startDayId, last4WeekPeriod.endDayId, true);
  return [dayIdFilter];
};

export const build4wComparisonRangeFilters = async () => {
  const { allWeekIdsByRetailerId, mainTimePeriod, retailer } = store.getState();
  const allWeekIds = allWeekIdsByRetailerId[retailer.id];
  const last4WeekPeriod = mainTimePeriod.availableMainTimePeriods.find((x) => x.id === '4w');
  const last4WeekComparisonPeriod = await computeComparisonTimePeriod(allWeekIds, last4WeekPeriod, 'prior-period');
  const dayIdFilter = buildDayIdRangeFilter(
    last4WeekComparisonPeriod.startDayId,
    last4WeekComparisonPeriod.endDayId,
    true
  );
  return [dayIdFilter];
};

export const moveForwardOneWeek = (dataPoint) => {
  // Assumption: we do not go between years when calling this
  dataPoint.weekId += 1;
  dataPoint.name = `${dataPoint.weekId}`;
  dataPoint.weekEnding = getWeekLastDate(dataPoint.weekId);
  dataPoint.weekEndingNextYear = getWeekLastDate(dataPoint.weekId + 100);
  return dataPoint;
};

export const transferWeekIdToLastDayOfWeek = (weekId, format) => {
  return moment(getWeekLastDate(weekId)).format(format);
};

export default {
  startYear,
  currentStartWeek,
  currentYear,
  isBeforeDay,
  isInclusivelyAfterDay,
  timeOffset,
  getWeekLastDate,
  getWeekLastDay,
  getWeekNumber,
  getWeekCount,
  monthNameOfWeek,
  parseDate,
  formatDate,
  getDateOfWeek,
  getDayIdFromDate
};
